import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { ShieldCheck, Lock, Clock, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

const schema = z.object({
  business_name: z.string().min(2, "Required"),
  contact_name: z.string().min(2, "Required"),
  email: z.string().email("Valid email required"),
  phone: z.string().min(7, "Valid phone required"),
  monthly_revenue: z.string().min(1, "Required"),
  time_in_business: z.string().min(1, "Required"),
  requested_amount: z.string().min(1, "Required"),
  industry: z.string().optional(),
  notes: z.string().optional(),
});

const REVENUE_OPTIONS = ["Under $10K", "$10K – $25K", "$25K – $50K", "$50K – $100K", "$100K – $250K", "$250K+"];
const TIB_OPTIONS = ["< 6 months", "6 – 12 months", "1 – 2 years", "2 – 5 years", "5+ years"];
const AMOUNT_OPTIONS = ["$10K – $25K", "$25K – $50K", "$50K – $100K", "$100K – $250K", "$250K – $500K", "$500K – $1M", "$1M+"];
const INDUSTRY_OPTIONS = ["Restaurants", "Retail", "Construction", "Medical", "Trucking", "E-commerce", "Other"];

export default function LeadForm() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    try {
      await api.post("/leads", data);
      toast.success("Application received. A specialist will reach out within 1 business hour.");
      setSubmitted(true);
      reset();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to submit. Please try again.");
    }
  };

  return (
    <section id="apply" data-testid="apply-section" className="py-24 md:py-32 bg-[#070F1B] border-y border-white/5 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-gold mb-5">Apply Now</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tighter">
            Get pre-approved in <span className="text-gold">60 seconds</span>.
          </h2>
          <p className="mt-6 text-white/60 text-lg leading-relaxed">
            One short form. A dedicated funding specialist on the phone within the hour. Most
            clients receive a same-day decision and funding within 24 hours.
          </p>

          <div className="mt-10 space-y-5">
            {[
              { icon: ShieldCheck, t: "Soft credit check only", b: "Won't affect your score" },
              { icon: Lock, t: "Bank-level encryption", b: "Your data is never sold or shared" },
              { icon: Clock, t: "Decision within hours", b: "Often same-day funding available" },
            ].map((it) => (
              <div key={it.t} className="flex items-start gap-4">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold shrink-0">
                  <it.icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-white font-semibold">{it.t}</div>
                  <div className="text-white/50 text-sm">{it.b}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 bg-[#0A1326]/80 backdrop-blur border border-white/10 p-8 md:p-12">
          {submitted ? (
            <div data-testid="apply-success" className="text-center py-10">
              <div className="w-16 h-16 mx-auto rounded-full bg-gold/15 border border-gold flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-heading text-3xl text-white">Application received.</h3>
              <p className="mt-3 text-white/60">A specialist will call you within 1 business hour to confirm the fastest path to capital.</p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="apply-reset"
                className="mt-8 inline-flex items-center gap-2 border border-white/20 hover:border-gold text-white px-6 py-3"
              >
                Submit another application
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid="apply-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Business Name" error={errors.business_name?.message}>
                  <input {...register("business_name")} className="dfc-input" data-testid="input-business-name" placeholder="Acme Restaurants LLC" />
                </Field>
                <Field label="Industry" error={errors.industry?.message}>
                  <select {...register("industry")} className="dfc-input" data-testid="input-industry">
                    <option value="">Select industry</option>
                    {INDUSTRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Field label="Monthly Revenue" error={errors.monthly_revenue?.message}>
                  <select {...register("monthly_revenue")} className="dfc-input" data-testid="input-monthly-revenue">
                    <option value="">Select range</option>
                    {REVENUE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Time in Business" error={errors.time_in_business?.message}>
                  <select {...register("time_in_business")} className="dfc-input" data-testid="input-time-in-business">
                    <option value="">Select</option>
                    {TIB_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Requested Amount" error={errors.requested_amount?.message}>
                  <select {...register("requested_amount")} className="dfc-input" data-testid="input-requested-amount">
                    <option value="">Select amount</option>
                    {AMOUNT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Contact Name" error={errors.contact_name?.message}>
                  <input {...register("contact_name")} className="dfc-input" data-testid="input-contact-name" placeholder="Jane Doe" />
                </Field>
                <Field label="Phone" error={errors.phone?.message}>
                  <input {...register("phone")} className="dfc-input" data-testid="input-phone" placeholder="(555) 123-4567" />
                </Field>
              </div>

              <Field label="Email" error={errors.email?.message}>
                <input {...register("email")} className="dfc-input" data-testid="input-email" placeholder="you@business.com" />
              </Field>

              <Field label="Notes (optional)">
                <textarea {...register("notes")} className="dfc-input min-h-[90px]" data-testid="input-notes" placeholder="Tell us anything that helps us fund you faster." />
              </Field>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="apply-submit"
                className="w-full bg-gold hover:bg-gold-400 disabled:opacity-60 text-navy-950 font-semibold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)]"
              >
                {isSubmitting ? "Submitting…" : "Get My Approval"}
              </button>
              <p className="text-xs text-white/40 text-center">
                By submitting you agree to our <a href="#" className="underline hover:text-gold">Terms</a> and <a href="#" className="underline hover:text-gold">Privacy Policy</a>. Soft credit check only.
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .dfc-input {
          width: 100%;
          background: #050B14;
          border: 1px solid rgba(255,255,255,0.12);
          color: #F8F9FA;
          padding: 14px 16px;
          font-size: 0.95rem;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          appearance: none;
        }
        .dfc-input:focus {
          outline: none;
          border-color: #D4AF37;
          box-shadow: 0 0 0 3px rgba(212,175,55,0.15);
        }
        .dfc-input::placeholder { color: rgba(255,255,255,0.3); }
        select.dfc-input { background-image: linear-gradient(45deg, transparent 50%, #D4AF37 50%), linear-gradient(135deg, #D4AF37 50%, transparent 50%); background-position: calc(100% - 18px) 50%, calc(100% - 12px) 50%; background-size: 6px 6px, 6px 6px; background-repeat: no-repeat; padding-right: 40px; }
      `}</style>
    </section>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.18em] uppercase font-semibold text-white/60 mb-2 block">{label}</span>
      {children}
      {error && <span className="text-xs text-red-400 mt-1.5 block" data-testid="field-error">{error}</span>}
    </label>
  );
}
